// NumberChakra.jsx
import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUndo, faLightbulb, faCheckCircle, faQuestionCircle, faTimes } from '@fortawesome/free-solid-svg-icons';

export default function NumberChakra({ youtubeLink }) {
  const [showHelp, setShowHelp] = useState(false);
  const [notification, setNotification] = useState(null);
  const [gridSize] = useState(9); // Fixed to 9x9
  const [grid, setGrid] = useState([]);
  const [history, setHistory] = useState([]);
  const [completed, setCompleted] = useState(false);
  const [solution, setSolution] = useState([]);

  // Generate random Sudoku-like solution
  const generateSolution = (size) => {
    return Array(size).fill(null).map(() =>
      Array(size).fill(null).map(() => Math.floor(Math.random() * size) + 1)
    );
  };

  // Initialize grid whenever gridSize changes
  useEffect(() => {
    setSolution(generateSolution(gridSize));
    setGrid(Array(gridSize).fill(null).map(() => Array(gridSize).fill('')));
    setHistory([]);
    setCompleted(false);
  }, [gridSize]);

  const handleChange = (r, c, val) => {
    if (!new RegExp(`^[1-${gridSize}]?$`).test(val)) return; // allow only valid numbers or empty
    const newGrid = grid.map(row => [...row]);
    newGrid[r][c] = val;
    setHistory([...history, grid]);
    setGrid(newGrid);
  };

  const undo = () => {
    if (!history.length) return;
    const last = history[history.length - 1];
    setHistory(history.slice(0, -1));
    setGrid(last);
  };

  const giveHint = () => {
    const emptyCells = [];
    for (let r = 0; r < gridSize; r++) {
      for (let c = 0; c < gridSize; c++) {
        if (parseInt(grid[r][c]) !== solution[r][c]) emptyCells.push([r, c]);
      }
    }
    if (!emptyCells.length) return;
    const [r, c] = emptyCells[Math.floor(Math.random() * emptyCells.length)];
    const newGrid = grid.map(row => [...row]);
    newGrid[r][c] = solution[r][c];
    setHistory([...history, grid]);
    setGrid(newGrid);
  };

  const checkSolution = () => {
    const isCorrect = grid.every((row, rIdx) =>
      row.every((cell, cIdx) => parseInt(cell) === solution[rIdx][cIdx])
    );
    setCompleted(isCorrect);
    if (!isCorrect) {
      setNotification({ type: 'error', message: '❌ Not correct yet. Keep trying!' });
      setTimeout(() => setNotification(null), 3000);
    }
  };

  return (
    <div className="p-2 min-h-screen bg-black text-gray-300 flex flex-col items-center">

      {/* Top Buttons */}
      <div className="w-full flex justify-center gap-4 items-center mb-4 flex-wrap">
        <button
          className="w-20 text-xs lg:text-m lg:w-40 px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 flex justify-center items-center gap-2"
          onClick={() => window.history.back()}
        >
          ← Back
        </button>

        <button
          className="w-16 text-xs lg:text-m lg:w-40 px-4 py-2 bg-blue-600 text-black rounded-lg hover:bg-blue-500 flex justify-center items-center gap-2"
          onClick={() => setShowHelp(true)}
        >
          <FontAwesomeIcon icon={faQuestionCircle} /> Help
        </button>
      </div>

      {/* Help Modal */}
      {showHelp && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-50 p-4">
          <div className="bg-gray-900 p-6 rounded-xl max-w-lg w-full text-gray-300 flex flex-col gap-4">
            <h3 className="text-m lg:text-xl font-bold text-green-400">📖 How to Play Number Chakra</h3>
            <p className='text-m lg:text-lg'>
              Fill the grid so that each row and column contains numbers 1 to {gridSize} without repeating. 
              Use hints or undo to help complete the puzzle.
            </p>
            <ul className="list-disc list-inside text-gray-400">
              <li>Click on a cell and type a number 1-{gridSize}.</li>
              <li>Use the Hint button to fill a correct number.</li>
              <li>Undo previous moves with the Undo button.</li>
              <li>Press Check when you think the puzzle is complete.</li>
            </ul>

            {youtubeLink && (
              <a
                href={youtubeLink}
                target="_blank"
                rel="noopener noreferrer"
                className="w-20 text-xs lg:text-m lg:w-40 px-4 py-2 bg-red-600 text-black rounded-lg hover:bg-red-500 text-center font-semibold"
              >
                ▶ Watch
              </a>
            )}

            <button
              className="w-20 text-xs lg:text-m lg:w-40 px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 mt-2"
              onClick={() => setShowHelp(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {completed && (
        <div className="text-green-400 text-xl flex items-center gap-2 mb-2 animate-pulse">
          <FontAwesomeIcon icon={faCheckCircle} /> Puzzle Completed!
        </div>
      )}

      <h2 className="text-lg lg:text-2xl font-bold mb-2 text-green-400">🧠 Number Chakra (Sudoku)</h2>
      <p className="text-gray-400 mb-4 text-center max-w-md text-xs lg:text-m">
        Fill the numbers 1-{gridSize} in the grid. Use hints and undo to complete the puzzle!
      </p>

      <div className="grid grid-cols-9 gap-0 bg-black max-w-2xl">
        {grid.flatMap((row, rIdx) =>
          row.map((cell, cIdx) => {
            const blockSize = Math.sqrt(gridSize); // 3 for 9x9 Sudoku
            // Thicker border for 3x3 blocks
            const borderTop = rIdx % blockSize === 0 ? '3px' : '1px';
            const borderLeft = cIdx % blockSize === 0 ? '3px' : '1px';
            const borderBottom = (rIdx + 1) % blockSize === 0 ? '3px' : '1px';
            const borderRight = (cIdx + 1) % blockSize === 0 ? '3px' : '1px';

            return (
              <input
                key={`${rIdx}-${cIdx}`}
                type="text"
                maxLength={1}
                value={cell}
                onChange={(e) => handleChange(rIdx, cIdx, e.target.value.toUpperCase().slice(-1))}
                className="w-full h-full text-center text-white font-bold bg-black focus:outline focus:outline-2 focus:outline-blue-500 focus:outline-offset-0 m-0 p-0"
                style={{
                  borderTop: `${borderTop} solid #3b82f6`,
                  borderLeft: `${borderLeft} solid #3b82f6`,
                  borderBottom: `${borderBottom} solid #3b82f6`,
                  borderRight: `${borderRight} solid #3b82f6`,
                  fontSize: 'min(5vw, 5vh, 2.5rem)',
                  lineHeight: 1,
                  boxSizing: 'border-box',
                }}
              />
            );
          })
        )}
      </div>

      {/* Buttons */}
      <div className="p-4 flex flex-row gap-4 flex-wrap justify-center">
        <button
          onClick={checkSolution}
          className="w-20 text-xs lg:text-m lg:w-40 px-2 py-2 bg-green-600 text-black font-semibold rounded-lg hover:bg-green-500 transition flex justify-center items-center gap-2"
        >
          <FontAwesomeIcon icon={faCheckCircle} /> Check
        </button>
        <button
          onClick={undo}
          className="w-20 text-xs lg:text-m lg:w-40 px-2 py-2 bg-gray-700 text-gray-300 font-semibold rounded-lg hover:bg-gray-600 transition flex justify-center items-center gap-2"
        >
          <FontAwesomeIcon icon={faUndo} /> Undo
        </button>
        <button
          onClick={giveHint}
          className="w-20 text-xs lg:text-m lg:w-40 px-2 py-2 bg-blue-600 text-black font-semibold rounded-lg hover:bg-blue-500 transition flex justify-center items-center gap-2"
        >
          <FontAwesomeIcon icon={faLightbulb} /> Hint
        </button>
      </div>

      {/* Notification */}
      {notification && (
        <div
          className={`fixed top-4 left-1/2 transform -translate-x-1/2 px-4 py-2 rounded-lg text-sm font-semibold ${
            notification.type === 'error' ? 'bg-red-700 text-white' : 'bg-green-600 text-black'
          } flex justify-between items-center shadow-lg z-50 w-full max-w-md`}
        >
          <span>{notification.message}</span>
          <button
            className="w-10 p-1 text-lg flex items-center justify-center bg-red-800"
            onClick={() => setNotification(null)}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}
    </div>
  );
}